import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { injectScript } from '../inject-script';
import { NgDialogModule } from '../ng-dialog.module';
import {
  ICalendlyBookingDialogData,
  ICalendlyBookingDialogResult,
  ICalendlyBookingPrefill,
  ICalendlyBookingUtm,
} from './calendly-booking-dialog.types';

const CALENDLY_SCRIPT_ID = 'calendly-widget-script';
const CALENDLY_SCRIPT_URL =
  'https://assets.calendly.com/assets/external/widget.js';

interface ICalendlyInitConfig {
  url: string;
  parentElement: HTMLElement;
  prefill?: ICalendlyBookingPrefill;
  utm?: ICalendlyBookingUtm;
}

interface ICalendlyGlobal {
  initInlineWidget: (config: ICalendlyInitConfig) => void;
}

interface ICalendlyEventMessage {
  event?: string;
  payload?: {
    event?: { uri?: string };
    invitee?: { uri?: string };
  };
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    Calendly?: ICalendlyGlobal;
  }
}

@Component({
  selector: 'pt-calendly-booking-dialog',
  templateUrl: './calendly-booking-dialog.component.html',
  styleUrls: ['./calendly-booking-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule],
  host: { class: 'flex h-full flex-col' },
})
export class CalendlyBookingDialogComponent
  implements AfterViewInit, OnDestroy
{
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _dialogRef =
    inject<
      MatDialogRef<CalendlyBookingDialogComponent, ICalendlyBookingDialogResult>
    >(MatDialogRef);

  @ViewChild('embedContainer', { static: true })
  private readonly _container!: ElementRef<HTMLDivElement>;

  private _messageHandler?: (event: MessageEvent) => void;

  readonly data = inject<ICalendlyBookingDialogData>(MAT_DIALOG_DATA);
  readonly loadError = signal(false);

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    this._subscribeToBookingEvents();
    void this._loadWidget();
  }

  ngOnDestroy(): void {
    if (this._messageHandler) {
      window.removeEventListener('message', this._messageHandler);
      this._messageHandler = undefined;
    }
  }

  private async _loadWidget(): Promise<void> {
    try {
      await injectScript(
        CALENDLY_SCRIPT_ID,
        CALENDLY_SCRIPT_URL,
        'text/javascript',
        true,
        false
      );
    } catch {
      this.loadError.set(true);
      return;
    }
    this._initWidget();
  }

  private _initWidget(): void {
    const calendly = window.Calendly;
    if (!calendly) {
      this.loadError.set(true);
      return;
    }
    calendly.initInlineWidget({
      url: this._buildUrl(),
      parentElement: this._container.nativeElement,
      prefill: this.data.prefill ?? {},
      utm: this.data.utm ?? {},
    });
  }

  private _buildUrl(): string {
    const params = new URLSearchParams();
    if (this.data.hideEventTypeDetails) {
      params.set('hide_event_type_details', '1');
    }
    if (this.data.hideLandingPageDetails) {
      params.set('hide_landing_page_details', '1');
    }
    if (this.data.hideGdprBanner) {
      params.set('hide_gdpr_banner', '1');
    }
    if (this.data.primaryColor) {
      params.set('primary_color', this.data.primaryColor);
    }
    if (this.data.textColor) {
      params.set('text_color', this.data.textColor);
    }
    if (this.data.backgroundColor) {
      params.set('background_color', this.data.backgroundColor);
    }
    const query = params.toString();
    if (!query) {
      return this.data.url;
    }
    const separator = this.data.url.includes('?') ? '&' : '?';
    return `${this.data.url}${separator}${query}`;
  }

  private _subscribeToBookingEvents(): void {
    this._messageHandler = (event: MessageEvent) => {
      if (!this._isCalendlyOrigin(event.origin)) {
        return;
      }
      const data = event.data as ICalendlyEventMessage | undefined;
      if (!data || data.event !== 'calendly.event_scheduled') {
        return;
      }
      this._dialogRef.close({
        scheduled: true,
        eventUri: data.payload?.event?.uri,
        inviteeUri: data.payload?.invitee?.uri,
      });
    };
    window.addEventListener('message', this._messageHandler);
  }

  private _isCalendlyOrigin(origin: string): boolean {
    try {
      const { protocol, hostname } = new URL(origin);
      return (
        protocol === 'https:' &&
        (hostname === 'calendly.com' || hostname.endsWith('.calendly.com'))
      );
    } catch {
      return false;
    }
  }
}
